<template>
  <ContainerScrolling class="event-details">
    <LayoutFill :no-gutters="solidHeader" :no-padding="solidHeader">
      <MainBanner :title="event.title" :solid-header="solidHeader" />
    </LayoutFill>
    <LayoutGutters>
      <v-row>
        <v-col cols="12" md="8" class="pt-0">
          <div class="event-details__heading">
            <h2
              v-if="eventLocation || eventDate"
              class="title font-weight-bold"
            >
              {{ eventLocation }}
              <br aria-hidden="true" />
              {{ eventDate }}
            </h2>
            <div class="body mr-2 mt-3">{{ event.description }}</div>
          </div>
        </v-col>

        <v-col cols="12" md="4" class="pt-0">
          <div>
            <v-btn
              v-if="event.status === `SUBMISSION_OPENED` && !disableSubmissions"
              :max-width="200"
              outlined
              rounded
              aria-label="submit content"
              block
              @click="initiateSubmit"
              >Submit Content</v-btn
            >
            <div class="mt-3">
              <img class="event-details__image" :src="imageUrl" alt="" />
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row v-if="hasSponsors" mt-4>
        <v-col>
          <EventSponsors
            title="An event in association with"
            :sponsors="event.sponsors"
            :show-large-title="false"
          />
        </v-col>
      </v-row>
      <v-row mt-4>
        <v-col>
          <SelectedArticles
            title="Latest Content"
            :articles="selectedEventArticles"
            :show-content-type="showContentTypeEvents"
            :show-large-title="false"
          />
        </v-col>
      </v-row>
      <!-- Temporary disabled in first version -->
      <v-row mt-4 mb-4>
        <v-col>
          <div class="text-center">
            <v-btn
              outlined
              rounded
              :to="`/engage/${partner}/event-details/event-submissions?event=${event.id}`"
            >
              View More
              <span class="d-sr-only">&nbsp;Latest Content</span>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </LayoutGutters>
  </ContainerScrolling>
</template>

<script>
import { mapState } from "vuex";
import buildActionDispatcher from "~/actions/utilities/buildActionDispatcher";
import mapFlags from "~/plugins/utilities/mapFlags";

import ContainerScrolling from "~/components/layout/ContainerScrolling";
import LayoutFill from "~/components/layout/LayoutFill";
import LayoutGutters from "~/components/layout/LayoutGutters";

import MainBanner from "~/components/banner/MainBanner.vue";
import SelectedArticles from "~/components/articles/SelectedArticles.vue";

import {
  displayIsMobile,
  displayIsTablet,
  displayIsDesktop,
} from "~/components/utilities/displayBreakpoints";

import localDate from "~/plugins/utilities/localDate";
import EventSponsors from "~/components/events/EventSponsors";

import getPageTitle from "~/plugins/utilities/getPageTitle.js";
import getSiteName from "~/plugins/utilities/getSiteName.js";
import toCanonicalUrl from "~/plugins/utilities/toCanonicalUrl.js";
import isUnauthorized from "~/plugins/utilities/isUnauthorized";
import getAssetUrl from "~/plugins/utilities/getAssetUrl.js";

export default {
  name: "EventDetails",
  components: {
    EventSponsors,
    ContainerScrolling,
    LayoutFill,
    LayoutGutters,
    MainBanner,
    SelectedArticles,
  },
  async fetch(context) {
    const { $config, params, redirect } = context;
    const partner = $config.partner;
    const data = await buildActionDispatcher("loadEventDetails")(context);
    const selectedEvent = data?.selectedEvent;
    if (!selectedEvent) {
      redirect(`/engage/${partner}/event-details/not-found/${params.id}`);
    }
  },
  head() {
    const { siteName, event, partner } = this;
    const title = event.title;

    const displayTitle = getPageTitle(siteName);

    const pageTitle = `${title} | ${displayTitle}`;
    const canonicalUrl = toCanonicalUrl(
      `event-details/${this.event.id}`,
      partner
    );

    return {
      title: pageTitle,
      meta: [{ name: "description", hid: "description", content: title }],
      link: [{ rel: "canonical", href: canonicalUrl }],
    };
  },
  computed: {
    ...mapState({
      layout: (state) => state.theme.layout,
      siteName: (state) => getSiteName(state.theme),
      event: (state) => state.selectedEvent,
      selectedEventArticles: (state) =>
        state.currentOrigin.selectedEventArticles,
    }),
    ...mapFlags(["showContentTypeEvents", "disableSubmissions"]),

    partner() {
      return this.$config.partner;
    },
    displayIsMobile,
    displayIsTablet,
    displayIsDesktop,
    solidHeader() {
      return this.layout.mainBannerSolidHeader;
    },
    hasSponsors() {
      return this.event.sponsors ? this.event.sponsors.length > 0 : null;
    },
    imageUrl() {
      return getAssetUrl(this.event.bannerAsset);
    },
    eventLocation() {
      return this.event.location
        ? this.eventDate
          ? `${this.event.location},`
          : this.event.location
        : "";
    },
    eventDate() {
      const { startDate, endDate } = this.event;
      let dates = null;

      if (startDate && endDate) {
        dates = `${localDate(startDate, "EEEE MMMM dd")}–${localDate(
          endDate,
          "EEEE MMMM dd yyyy"
        )}`;
      } else if (startDate) {
        dates = localDate(startDate, "EEEE MMMM dd yyyy");
      }

      return dates;
    },
  },
  methods: {
    initiateSubmit({ currentTarget }) {
      const redirectTo = `/engage/${this.partner}/submit-process/step-one?eventId=${this.event.id}`;

      if (isUnauthorized(this.$store.state.user, "USER")) {
        this.$root.$emit(
          "show-login-options-redirect",
          redirectTo,
          currentTarget
        );
      } else {
        this.$router.push({
          path: redirectTo,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.event-details {
  &__heading {
    word-break: normal;
    overflow-wrap: break-word;
    overflow: hidden;
  }
  &__image {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    cursor: pointer;
  }
}
</style>
